import { useEffect, useState } from 'react';

import { executeQuery } from 'common/gql';
import type {
  OrdersContactFormQuery,
  OrdersContactFormQueryVariables,
} from 'common/gql/generated/schema';
import { useGQLLocale } from 'common/hooks/locale';
import { useLocalization } from 'common/providers/LocaleProvider';
import { localizeDate } from 'common/utils/date';
import { captureException } from 'common/utils/error-reporter';
import ordersQuery from '../gql/query/orders.gql';
import type { ContactFormOrder } from '../types';

const ORDERS_PAGE_SIZE = 6;
const INITIAL_PAGE = 0;
const INITIAL_TOTAL_COUNT = 0;

export interface useFetchDrawerOrdersOptions {
  pageSize?: number;
  initialPage?: number;
  setDrawerError: () => void;
}

const useFetchDrawerOrders = ({
  pageSize = ORDERS_PAGE_SIZE,
  initialPage = INITIAL_PAGE,
  setDrawerError,
}: useFetchDrawerOrdersOptions) => {
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState<ContactFormOrder[]>([]);
  const [page, setPage] = useState(initialPage);
  const [totalCount, setTotalCount] = useState(INITIAL_TOTAL_COUNT);
  const [hasFetchError, setHasFetchError] = useState(false);
  const locale = useGQLLocale();

  const fetchDrawerOrders = async (pageNumber: number) => {
    setIsLoading(true);
    const isInitialFetch = pageNumber === initialPage;

    try {
      const { user } = await executeQuery<OrdersContactFormQuery, OrdersContactFormQueryVariables>({
        query: ordersQuery,
        variables: {
          locale,
          first: pageSize,
          offset: pageNumber * pageSize,
        },
      });

      return { orders: user.orders.orders, totalCount: user.orders.totalCount };
    } catch (error) {
      captureException(error);
      if (isInitialFetch) {
        setDrawerError();
      } else {
        setHasFetchError(true);
      }

      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const ordersData = await fetchDrawerOrders(page);
      if (ordersData) {
        setOrders(ordersData.orders);
        setTotalCount(ordersData.totalCount);
      }
    })();
  }, []);

  const fetchMore = async () => {
    const newPage = page + 1;
    const ordersData = await fetchDrawerOrders(newPage);

    if (!ordersData) {
      return;
    }

    setHasFetchError(false);
    setOrders(prevOrders => [...prevOrders, ...ordersData.orders]);
    setPage(newPage);
  };

  return {
    orders,
    isLoading,
    fetchMore,
    nextPage: page + 1,
    hasMoreOrders: (page + 1) * pageSize < totalCount,
    hasFetchError,
  };
};

const useOrderSummary = (orderNumber: string, orders: ContactFormOrder[]) => {
  const { t, fc, locale } = useLocalization();
  if (!orderNumber || !orders.length) {
    return '';
  }

  const selectedOrder = orders.find(
    ({ id, oms }) => oms?.orderId === orderNumber || id === orderNumber
  );
  if (!selectedOrder) {
    return '';
  }
  const { id, grandTotal, createdAt, oms } = selectedOrder;
  const orderDate = localizeDate(createdAt, locale);

  return `${t('h24_order_number')}: ${oms?.orderId ?? id}, ${fc(grandTotal)}, ${orderDate}`;
};

export { useOrderSummary, useFetchDrawerOrders };
